"use client";

import React, { useRef, useLayoutEffect, useState, useCallback, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import AutosizeTextArea from "react-textarea-autosize";
import {
  Box,
  Flex,
  Text,
  Button,
  PopupMenu,
  utils,
  focusAllOfInput,
  focusEndOfInput,
  useBoolean,
  useClipboard,
  useSubmitTextKeys,
  useCodeHighlighting,
  useEditorColors,
  codeLangs,
} from "@thenounproject/lingo-core";

import { CanvasComponentProps, CanvasEditableProps } from "../sharedProps";
import { getVersionedItemId } from "@src/helpers/items";
import { insertData, InsertType } from "@constants/InsertType";
import { getCanvasLayoutProperties } from "../utils";
import { SelectionWrapper } from "../SelectionWrapper";
import { CanvasItemWrapper } from "../CanvasItemWrapper";
import { CodeSnippetTheme } from "./CodeSnippetTheme";

const Wrapper = styled(Box)<{ selected?: boolean; canEdit?: boolean }>`
  border: 1px solid ${p => (p.selected && p.canEdit ? "transparent" : utils.getColor("border"))};
  border-radius: 4px;
  width: 100%;
  ${utils.showOnHoverCSS("show-on-hover")}
`;

const ActionContainer = styled(Flex).attrs({
  position: "absolute",
  top: "4px",
  right: "4px",
  gap: "8px",
  transition: "opacity 0.1s ease-in-out",
})`
  button {
    box-shadow: 0 1px 3px 0 rgba(36, 36, 37, 0.12) !important;
  }
`;

const OverflowButton = styled(Button).attrs({
  className: "action-button",
  size: "small",
  buttonStyle: "secondary",
})``;

const Pre = styled(Text).attrs<{ backgroundColor?: string }>({
  as: "pre",
  p: "none",
  background: "transparent",
})<{ backgroundColor?: string }>`
  ${CodeSnippetTheme}
`;

const Code = styled(Text).attrs({
  as: "code",
  font: "gallery.code",
  p: "none",
})`
  word-break: break-word;
`;

const EditorWrapper = styled(Box)<{ canEdit?: boolean }>`
  background: transparent;
  width: 100%;
  ${p => p.canEdit && `cursor: text;`}
`;

const Textarea = styled(AutosizeTextArea)`
  min-height: 18px;
  padding: 0;
  margin: 0;
  background: transparent;
  border: none;
  width: 100%;
  resize: none;
  vertical-align: top;
  ${utils.getFont("gallery.code", null)};
  &:focus {
    outline: none;
  }
`;

type Props = CanvasComponentProps & CanvasEditableProps;

const CanvasCodeSnippet: React.FC<Props> = ({
  item,
  isSelected,
  onClick,
  onDoubleClick: _onDoubleClick,
  onOverflowSelected,
  onDragEnd,
  onDragStart,
  draggable,
  canEdit,
  isEditing,
  onFinishEditing,
  onBeginEditing,
  index,
  containerWidth,
  ...rest
}) => {
  const { outerWidth, size } = getCanvasLayoutProperties({ item, containerWidth });
  const versionedId = getVersionedItemId(item);
  const hasDefaultValue = item.data.content === insertData[InsertType.code].data.content;

  const { content, codeLanguage } = item.data;

  const [editValue, setEditValue] = useState(content);
  const [minHeight, setMinHeight] = useState<string | null>(null);
  const [isLanguageMenuOpen, openLanguageMenu, closeLanguageMenu] = useBoolean(false);

  const codeRef = useCodeHighlighting(content, codeLanguage);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const theme = useTheme();
  const { onCopy, hasCopied } = useClipboard(content);
  const onKeyDown = useSubmitTextKeys(true);

  const { backgroundColor, codeSnippetColor, color } = useEditorColors(
    item.displayProperties.cardBackgroundColor,
    theme
  );

  const wrapperId = `${item.id}-code-snippet`;
  const languageMenuId = `language-menu-${item.id}`;

  // Sync edit value and focus
  useEffect(() => {
    setEditValue(content);
    if (isEditing && editorRef.current) {
      (hasDefaultValue ? focusAllOfInput : focusEndOfInput)(editorRef.current);
    }
  }, [content, isEditing, hasDefaultValue]);

  // Set min-height to prevent layout shift
  useLayoutEffect(() => {
    const heightTimeout = setTimeout(() => {
      const wrapperEl = document.getElementById(wrapperId);
      if (!wrapperEl) return;
      setMinHeight(isEditing ? null : `${wrapperEl.clientHeight}px`);
    }, 100);
    return () => clearTimeout(heightTimeout);
  }, [wrapperId, editValue, item, isEditing]);

  // Event handlers
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onClick(item, e);
    },
    [onClick, item]
  );

  const handleOverflowClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOverflowSelected(item);
    },
    [item, onOverflowSelected]
  );

  const handleBeginEditing = useCallback(() => {
    onBeginEditing?.(item.id);
  }, [item.id, onBeginEditing]);

  const handleFinishEditing = useCallback(() => {
    if (!isEditing || !canEdit || !onFinishEditing) return;
    const val = editValue.trim();
    if (content.trim() === val) {
      return onFinishEditing(item, null);
    }
    onFinishEditing(item, { data: { content: val, codeLanguage } });
  }, [isEditing, canEdit, onFinishEditing, editValue, content, codeLanguage, item]);

  const handleUpdateLanguage = useCallback(
    (value: string) => {
      if (!canEdit || !onFinishEditing || codeLanguage === value) return;
      onFinishEditing(item, { data: { content, codeLanguage: value } });
    },
    [canEdit, onFinishEditing, codeLanguage, content, item]
  );

  // Conditional elements
  const titleElement = canEdit ? (
    <Button
      data-testid="gallery-code-snippet-select"
      buttonStyle="tertiary"
      themeOverrides={{
        primaryColor: codeSnippetColor,
        primaryColorDark: codeSnippetColor,
      }}
      icon="action.expand"
      text={codeLanguage ?? "Auto"}
      onClick={openLanguageMenu}
      fontStyle="ui.smallBold"
      size="small"
      dataPopupSource={languageMenuId}
    />
  ) : codeLanguage && codeLanguage.toLowerCase() !== "auto" ? (
    <Text color={codeSnippetColor} font="ui.smallBold">
      {codeLanguage}
    </Text>
  ) : null;

  const editorElement = isEditing ? (
    <Textarea
      ref={editorRef}
      data-testid="gallery-code-snippet-editor"
      aria-label="Update code snippet"
      onBlur={handleFinishEditing}
      onKeyDown={onKeyDown}
      onChange={e => setEditValue(e.target.value)}
      value={editValue}
      maxRows={1000}
      style={{ color }}
    />
  ) : (
    <Pre backgroundColor={backgroundColor}>
      <Code color={color} ref={codeRef}>
        {content}
      </Code>
    </Pre>
  );

  const languageMenu = isLanguageMenuOpen && (
    <PopupMenu close={closeLanguageMenu} source={languageMenuId}>
      {codeLangs.map(lang => (
        <PopupMenu.Item
          key={lang}
          data-testid={`code-language-${lang}`}
          checked={codeLanguage === lang}
          title={lang}
          onClick={() => {
            handleUpdateLanguage(lang);
            closeLanguageMenu();
          }}
        />
      ))}
    </PopupMenu>
  );

  return (
    <CanvasItemWrapper
      data-full-width={outerWidth === "100%" || size === 1}
      outerWidth={outerWidth}
      size={size}
      index={index}
      itemId={item.id}
      {...rest}>
      <Box
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        id={item.id}
        data-itemid={versionedId}
        data-dragid={versionedId}
        data-shortid={item.shortId}
        position="relative"
        className="hljs-theme-dark">
        <SelectionWrapper selected={isSelected}>
          <Wrapper
            data-selected={isSelected}
            selected={isSelected}
            canEdit={canEdit}
            background={backgroundColor}
            data-testid="gallery-code-snippet-parent">
            {languageMenu}
            <Box onClick={canEdit ? handleClick : null}>
              <Flex flexDirection="column" gap="10px" p="10px 8px" alignItems="flex-start">
                {titleElement}
                <EditorWrapper
                  id={wrapperId}
                  data-testid="gallery-code-snippet-wrapper"
                  minHeight={minHeight}
                  canEdit={canEdit}
                  onDoubleClick={canEdit ? handleBeginEditing : undefined}>
                  {editorElement}
                </EditorWrapper>
              </Flex>
            </Box>
            <ActionContainer className="show-on-hover">
              {onOverflowSelected && (
                <OverflowButton
                  dataPopupSource={`overflow-menu-${item.id}`}
                  buttonStyle="secondary"
                  icon="navigation.overflow"
                  onClick={handleOverflowClick}
                />
              )}
              <Button onClick={onCopy} size="small" text={hasCopied ? "Copied" : "Copy"} />
            </ActionContainer>
          </Wrapper>
        </SelectionWrapper>
      </Box>
    </CanvasItemWrapper>
  );
};

export default CanvasCodeSnippet;
